import React, {
  FunctionComponent, useEffect, useState, Fragment,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColGroupDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import {
  UtimacoPacketSwitch,
  UtimacoPacketSwitchPrivileges,
} from 'data/utimacoPacketSwitch/utimacoPacketSwitch.types';
import * as actions from 'data/utimacoPacketSwitch/utimacoPacketSwitch.actions';
import * as selectors from 'data/utimacoPacketSwitch/utimacoPacketSwitch.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as accessFunctionActions from 'data/accessFunction/accessFunction.actions';
import * as collectionFunctionGroupActions from 'data/collectionFunctionGroup/collectionFunctionGroup.actions';
import * as collectionFunctionActions from 'data/collectionFunction/collectionFunction.actions';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import * as collectionFunctionSelectors from 'data/collectionFunction/collectionFunction.selectors';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { AppToaster } from 'shared/toaster';
import cloneDeep from 'lodash.clonedeep';
import UtimacoPacketSwitchListGrid from './components/UtimacoPacketSwitchListGrid';
import { distributorColumnDefs } from '../../UtimacoPacketSwitch.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  deliveryFunctionId: state.global.selectedDF,
  data: selectors.getUtimacoPacketSwitchList(state),
  selectedUtimacoPacketSwitch: selectors.getSelectedUtimacoPacketSwitch(state),
  userDBId: state.authentication.userDBId,
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
  collectionFunctions: collectionFunctionSelectors.getCollectionFunctionList(state),
  collectionFunctionGroups: collectionFunctionGroupSelectors.getCollectionFunctionGroupList(state),
});

const mapDispatch = {
  goToUtimacoPacketSwitchCreate: actions.goToUtimacoPacketSwitchCreate,
  goToUtimacoPacketSwitchEdit: actions.goToUtimacoPacketSwitchEdit,
  goToUtimacoPacketSwitchView: actions.goToUtimacoPacketSwitchView,
  selectUtimacoPacketSwitch: actions.selectUtimacoPacketSwitch,
  getUtimacoPacketSwitchList: actions.getUtimacoPacketSwitchList,
  deleteUtimacoPacketSwitch: actions.deleteUtimacoPacketSwitch,
  getAccessFunctions: accessFunctionActions.getAccessFunctionsWithViewOptions,
  getCollectionFunctionGroups: collectionFunctionGroupActions.getCollectionFunctionGroupList,
  fetchCollectionFunctionList: collectionFunctionActions.fetchCollectionFunctionList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const UtimacoPacketSwitchList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  history,
  beforeLogout,
  privileges,
  deliveryFunctionId,
  data,
  selectedUtimacoPacketSwitch,
  accessFunctions,
  collectionFunctions,
  collectionFunctionGroups,
  goToUtimacoPacketSwitchCreate,
  goToUtimacoPacketSwitchEdit,
  goToUtimacoPacketSwitchView,
  selectUtimacoPacketSwitch,
  getUtimacoPacketSwitchList,
  deleteUtimacoPacketSwitch,
  getAccessFunctions,
  getCollectionFunctionGroups,
  fetchCollectionFunctionList,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [dataGrid, setDataGrid] = useState<UtimacoPacketSwitch[] | []>([]);
  const [columnDefs] = useState<ColGroupDef[]>(distributorColumnDefs);

  useEffect(() => {
    getUtimacoPacketSwitchList(deliveryFunctionId);
    getAccessFunctions(deliveryFunctionId);
    getCollectionFunctionGroups(deliveryFunctionId);
    fetchCollectionFunctionList(deliveryFunctionId);
    return () => {
      AppToaster.clear();
    };
  }, [
    deliveryFunctionId,
    getUtimacoPacketSwitchList,
    getAccessFunctions,
    getCollectionFunctionGroups,
    fetchCollectionFunctionList,
  ]);

  useEffect(() => {
    if (data) {
      const clonedData = cloneDeep(data);

      const formattedData = clonedData.map((packetSwitch) => {
        const accessFunctionName = accessFunctions.find((af) => atob(af.id as string) === packetSwitch.AFID)?.name;
        const cfName = collectionFunctions.find((cf) => atob(cf.id as string) === packetSwitch.CFID)?.name;

        return {
          ...packetSwitch,
          AFID: accessFunctionName as string,
          CFID: cfName as string
        };
      });

      setDataGrid(formattedData as UtimacoPacketSwitch[]);
    }
  }, [accessFunctions, collectionFunctions, collectionFunctionGroups, data]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  const handleRefresh = () => getUtimacoPacketSwitchList(deliveryFunctionId);

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToUtimacoPacketSwitchCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectUtimacoPacketSwitch(event.api.getSelectedRows());
  };

  const handleShowActionMenuItem = () => true;

  const handleEdit = () => goToUtimacoPacketSwitchEdit(history);

  const handleSelectItemMenuAction = () => goToUtimacoPacketSwitchView(history);

  const handleDelete = () => {
    const distributorConfigrationId = selectedUtimacoPacketSwitch[0]?.id;
    if (distributorConfigrationId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        selectedUtimacoPacketSwitch[0]?.IPADDR,
        AuditService.UTIMACO_PACKET_SWITCH,
        AuditActionType.UTIMACO_PACKET_SWITCH_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteUtimacoPacketSwitch(deliveryFunctionId, distributorConfigrationId, body);
  };

  const hasPrivilegeToView = privileges.includes(UtimacoPacketSwitchPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(UtimacoPacketSwitchPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(UtimacoPacketSwitchPrivileges.Delete);

  return (
    <Fragment>
      <UtimacoPacketSwitchListGrid
        data={dataGrid}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onSelectItemMenuAction={handleSelectItemMenuAction}
        onShowActionMenuItem={handleShowActionMenuItem}
        lastUpdatedAt={lastUpdatedAt}
        selectedUtimacoPacketSwitch={selectedUtimacoPacketSwitch[0] || []}
        onDeleteRow={handleDelete}
        onEdit={handleEdit}
      />
    </Fragment>
  );
};

export default withRouter(connector(UtimacoPacketSwitchList));
